import React, { useState, useEffect } from 'react'
import { Classes } from '@blueprintjs/core'
import { IconButton, Confirmation, Toaster, Button } from '@ui'
import { NavigateFn } from '@reach/router'
import ImportProgress from './ImportProgress'
import SelectFileStep from '../../ImportFlow/SelectFileStep'
import useActiveImport from './hooks/useActiveImport'
import useFileReader from '../../ImportFlow/hooks/useFileReader'
import ImportSummary from '../../ImportFlow/ImportSummary'
import api from '@api'
import { useReload } from '@hooks/reloadContext'
import { NeighborhoodImportFragment } from '@api/fragments'
import gql from 'graphql-tag'
import { Dialog } from '@ui'
// import track from '@track'
// import Event from '@track/Event'

enum View {
  FileSelect,
  Progress,
  Summary,
}

interface NeighborhoodImportDialog {
  navigate: NavigateFn
  reload?: () => void
}

export default function NeighborhoodImportFlow({ navigate, reload }: NeighborhoodImportDialog) {
  const [dirty, setDirty] = useState(false)
  const { activeImport } = useActiveImport()
  const [importId, setImportId] = useState('')
  const [view, setView] = useState<View>(View.FileSelect)
  const { file, setFile, fileContent } = useFileReader()
  const [importing, setImporting] = useState(false)
  const reloader = useReload()

  useEffect(() => {
    if (activeImport) {
      setImportId(activeImport.id)
      setView(View.Progress)
    }
  }, [activeImport])

  function onClose() {
    navigate && navigate('?newItem')
  }

  return (
    <Dialog
      isOpen={true}
      style={{ position: 'relative', minWidth: 500 }}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      transitionDuration={200}
      isCloseButtonShown={false}
      title={view === View.FileSelect ? 'Import Neighborhood Employees' : ''}
      toolbar={
        <div className="dialog-buttons">
          <IconButton minimal round tooltip="Close" iconName="times" onClick={onClose} />
        </div>
      }
      body={
        <div className={Classes.DIALOG_BODY}>
          {view === View.FileSelect ? (
            <SelectFileStep
              file={file}
              templateApi={api.neighborhood.import.template}
              downloadFile="neighborhood_template.csv"
              downloadText="for bulk operation neighborhood employee import."
              importText="You can import multiple or a single neighborhood employees."
              onChange={(file) => {
                setFile(file)
                setDirty(true)
              }}
            />
          ) : view === View.Progress ? (
            <ImportProgress
              {...{
                file,
                importId,
                callback: (error) => {
                  if (error) {
                    setView(View.FileSelect)
                    Toaster.fail({ error })
                    return
                  }
                  reload?.()
                  reloader.neighborhoods()
                  setView(View.Summary)
                },
              }}
            />
          ) : view === View.Summary ? (
            <ImportSummary
              id={importId}
              onSummary={(id) => api.import.summary(id, query)}
              onClose={onClose}
            />
          ) : null}
        </div>
      }
      footer={
        <div className={`${Classes.DIALOG_FOOTER} flex justify-end mt-1`}>
          {view === View.FileSelect && (
            <Button
              disabled={!file}
              text="Import"
              loading={importing}
              onClick={() => {
                setImporting(true)
                setView(View.Progress)
                api.import.upload({ file: fileContent }, 'employees_neighborhoods').then((data) => {
                  setImportId(data.id)
                  setDirty(false)
                })
              }}
            />
          )}
        </div>
      }
    ></Dialog>
  )
}

const query = gql`
  query node($id: ID!) {
    node(id: $id) {
      ... on EmployeesNeighborhoodsImport {
        ...NeighborhoodImportFragment
      }
    }
  }
  ${NeighborhoodImportFragment}
`
